import { type AttachedFilesRequest } from "legacyComponents/FileUploadContainer.types";

import { EntityIdJobIdResponse } from "types/apiResponse/apiResponse";
import { EmptyResponseEntity } from "types/emptyResponseEntity";

import { APIHandler } from "utilities/apiHandler";

import { type EditorContent } from "commonComponents/btWrappers/editor/editor.types";

import { SpecificationStatus } from "./common/SpecificationStatus";
import {
    type ISpecificationFormValues,
    type SpecificationOwnerShareMode,
    type SpecificationSubShareMode,
} from "./Specification.api.types";

export interface ISpecificationHandler {
    update(formData: SpecificationUpdateRequest): Promise<EntityIdJobIdResponse>;
    create(formData: SpecificationSaveRequest): Promise<EntityIdJobIdResponse>;
    delete(specificationJobIds: { jobId: number; id: number }): Promise<EmptyResponseEntity>;
}

export class SpecificationHandler implements ISpecificationHandler {
    async update(formData: SpecificationUpdateRequest): Promise<EntityIdJobIdResponse> {
        return await APIHandler(`/api/Specifications`, {
            method: "PUT",
            data: formData,
            responseType: EntityIdJobIdResponse,
        });
    }

    async create(formData: SpecificationSaveRequest): Promise<EntityIdJobIdResponse> {
        return await APIHandler(`/api/Specifications`, {
            method: "POST",
            data: formData,
            responseType: EntityIdJobIdResponse,
        });
    }

    async delete(specificationJobIds: { jobId: number; id: number }): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/Specifications", {
            method: "DELETE",
            data: {
                jobSpecificationIdsList: [specificationJobIds],
            },
            responseType: EmptyResponseEntity,
        });
    }
}

export class SpecificationSaveRequest {
    constructor(jobId: number, specificationFormValues: ISpecificationFormValues) {
        this.jobId = jobId;
        this.content = specificationFormValues.specificationContent;
        this.ownerShareMode = specificationFormValues.ownerShareMode;
        this.subShareMode = specificationFormValues.subShareMode;
        this.attachedFiles = specificationFormValues.attachedFiles;
        this.usersToNotify = specificationFormValues.usersToNotify ?? [];
    }
    jobId: number;
    content: EditorContent;
    ownerShareMode: SpecificationOwnerShareMode;
    subShareMode: SpecificationSubShareMode;
    attachedFiles?: AttachedFilesRequest;
    usersToNotify: number[];
}

export class SpecificationUpdateRequest {
    constructor(
        id: number,
        jobId: number,
        specificationFormValues: ISpecificationFormValues,
        status: SpecificationStatus = SpecificationStatus.Draft,
    ) {
        this.id = id;
        this.jobId = jobId;
        this.content = specificationFormValues.specificationContent;
        this.ownerShareMode = specificationFormValues.ownerShareMode;
        this.subShareMode = specificationFormValues.subShareMode;
        this.attachedFiles = specificationFormValues.attachedFiles;
        this.status = status;
        this.usersToNotify = specificationFormValues.usersToNotify ?? [];
    }
    id: number;
    jobId: number;
    content: EditorContent;
    ownerShareMode: SpecificationOwnerShareMode;
    subShareMode: SpecificationSubShareMode;
    attachedFiles?: AttachedFilesRequest;
    status: SpecificationStatus;
    usersToNotify: number[];
}
